import * as React from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import type { Conditional } from './misc';
import { isTruthy } from './misc';

interface ScrollToTarget {
  (args: {
    targetId?: string,
    beforeScroll?: (target: HTMLElement) => void,
  }): void;
}
export const goToTarget: ScrollToTarget = ({ targetId, beforeScroll }) => {
  const target = targetId && document.getElementById(targetId);
  if (target) {
    beforeScroll?.(target);
    target?.scrollIntoView({ behavior: 'smooth' });
  }
};

export function useHash() {
  const [hash, setHash] = React.useState('');
  React.useEffect(() => {
    function handleHashChange() {
      setHash(window.location.hash);
    }
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange, false);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
  return hash;
}

export function useSyncHashToSectionId(entries: Readonly<Array<Conditional<IntersectionObserverEntry>>>) {
  React.useEffect(() => {
    const entriesInView = entries.filter(isTruthy);
    const verticalCenter = window.innerHeight * 0.5;
    const centerEntry = entriesInView.find(entry => {
      const { y, height } = entry.boundingClientRect;
      return y < verticalCenter && y + height > verticalCenter;
    });
    const targetId = centerEntry?.target?.id;
    if (targetId && `#${targetId}` !== window.location.hash) {
      window.history.replaceState(window.history.state, document.title, `#${targetId}`);
      window.dispatchEvent(new Event('hashchange'));
    }
  }, [...entries]);
}

export function useSearchParams() {
  const [searchParams, setSearchParams] = React.useState(() => new URLSearchParams());
  React.useLayoutEffect(() => {
    setSearchParams(new URLSearchParams(window.location.search));
  }, []);
  return searchParams;
};

export function useScrollLock(lock: boolean = true) {
  const root = React.useRef<HTMLElement>();
  React.useLayoutEffect(() => {
    root.current  = document.getElementById('___gatsby')!;
  }, []);

  React.useEffect(() => {
    const $root = root.current;
    if (!$root) return;

    lock && disableBodyScroll($root);
    return () => void enableBodyScroll($root);
  }, [lock]);
}

export type ShareData = {
  url: URL,
  title?: string,
  text?: string,
};

type UseShareProps = {
  onFallback?: (url: URL) => void,
  onCancel?: (url: URL) => void,
};

export function useShare({ onFallback, onCancel }: UseShareProps) {
  return React.useCallback(async ({ url, title, text }: ShareData) => {
    if (!('share' in navigator)) {
      onFallback?.(url);
      return;
    }

    // 우리 중 그 누구도 Mac OS의 Share 구현이 좋다고 생각하지 않았다...
    if (navigator.userAgent.match(/Macintosh/i) && navigator.userAgent.match(/Safari/i)) {
      onFallback?.(url);
      return;
    }

    try {
      await navigator.share({
        url: url.href,
        title,
        text,
      });
    } catch(e) {
      if (e instanceof Error) {
        if (e.name === 'NotAllowedError') {
          onFallback?.(url);
          return;
        }

        onCancel?.(url);
      } else {
        console.error('Unknown error occurred', e);
      }
    }
  }, [onFallback, onCancel]);
}
